import { CloseOutlined } from '@ant-design/icons';
import {
  AppAdminFormLabel,
  AppButton,
  AppCard,
  AppModal,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import InsuranceCompanySearch from './InsuranceCompanySearch';
import PresignedUploadField, {
  type UploadFieldValue,
} from './PresignedUploadField';
import { RA2NDatePicker } from './RA2NDatePicker';
import { useInsurerFieldState } from '@/hooks/admin/useInsurerFieldState';
import { getInsurerList } from '@/hooks/admin/useAdminInsurerList';
import useAppToast from '@/hooks/useAppToast';
import { useTheme } from '@/shared/providers/theme';
import { useParams } from 'react-router-dom';
import {
  insurerAttentionUpdateSchema,
  type InsurerAttentionUpdateFormData,
} from '@/schemas/insurerAttentionUpdateSchema';
import { dateValueToIso } from '@/utils/dateConverter';
import {
  AdminInsurerAttention,
  AdminInsurerAttentionUpdateForm,
} from '@/types/admin-insurer-attention';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

const ATTACHMENT_COUNT = 5;

interface Props {
  record: AdminInsurerAttention;
  prefectureOptions: { value: number; label: string }[];
  isPending: boolean;
  onSubmit: (data: AdminInsurerAttentionUpdateForm) => void;
  onDelete?: () => void;
  onReflect?: () => void;
  onBack: () => void;
}

const AdminInsurerAttentionEditForm = ({
  record,
  prefectureOptions,
  isPending,
  onSubmit,
  onDelete,
  onReflect,
  onBack,
}: Props) => {
  const { error: toastError } = useAppToast();
  const { colors } = useTheme();
  const { site } = useParams<{ site: string }>();
  const siteType = site ?? 'medix';

  const {
    insurerId,
    insurerDisplay,
    insurerTypeName,
    insurerName,
    insurerError,
    setInsurerError,
    isInsurerModalOpen,
    setIsInsurerModalOpen,
    isOpeningModal,
    setInsurerDisplay,
    handleInsurerSelect,
    handleOpenInsurerModal,
    resolveInsurer,
    resetInsurerField,
  } = useInsurerFieldState(siteType);

  /** Value of each attachment slot. null = empty (or deleted by user). */
  const [newRefs, setNewRefs] = useState<(UploadFieldValue | null)[]>(
    Array(ATTACHMENT_COUNT).fill(null)
  );
  /** Filename of the previously-saved attachment on this record (for display only). */
  const [currentNames, setCurrentNames] = useState<(string | null)[]>(
    Array(ATTACHMENT_COUNT).fill(null)
  );
  /** True when the user clicks × on an existing attachment to mark it for removal. */
  const [deleted, setDeleted] = useState<boolean[]>(Array(ATTACHMENT_COUNT).fill(false));
  /** Per-slot uploading flag so we can disable submit while any slot is mid-upload. */
  const [uploadingFlags, setUploadingFlags] = useState<boolean[]>(
    Array(ATTACHMENT_COUNT).fill(false)
  );
  /** Per-slot client-side validation error flag (invalid ext / size). */
  const [uploadInvalidFlags, setUploadInvalidFlags] = useState<boolean[]>(
    Array(ATTACHMENT_COUNT).fill(false)
  );

  const isUploading = uploadingFlags.some(Boolean);
  const hasUploadValidationError = uploadInvalidFlags.some(Boolean);

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useTrimmedForm<InsurerAttentionUpdateFormData>({
    resolver: zodResolver(insurerAttentionUpdateSchema),
    defaultValues: { prefecture_id: '', publish_date: '', remarks: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    resetInsurerField({
      insurerId: record.insurer_id,
      insurerDisplay: record.insurer_number ?? '',
      insurerTypeName: record.insurer_type_name ?? '',
      insurerName: record.insurer_name ?? '',
    });
    reset({
      prefecture_id: record.prefecture_id ? String(record.prefecture_id) : '',
      publish_date: record.publish_date ?? '',
      remarks: record.remarks ?? '',
    });
    setCurrentNames(
      Array.from(
        { length: ATTACHMENT_COUNT },
        (_, i) => (record[`attachment${i + 1}` as keyof typeof record] as string | null) ?? null
      )
    );
    setDeleted(Array(ATTACHMENT_COUNT).fill(false));
    setNewRefs(Array(ATTACHMENT_COUNT).fill(null));
    setUploadingFlags(Array(ATTACHMENT_COUNT).fill(false));
    setUploadInvalidFlags(Array(ATTACHMENT_COUNT).fill(false));
  }, [record, reset]);

  const handleAttachmentDelete = (index: number) => {
    setDeleted(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  const handleSlotChange = (index: number, value: UploadFieldValue | null) => {
    setNewRefs(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSlotUploading = (index: number, flag: boolean) => {
    setUploadingFlags(prev => {
      const next = [...prev];
      next[index] = flag;
      return next;
    });
  };

  const handleSlotValidationError = (index: number, hasError: boolean) => {
    setUploadInvalidFlags(prev => {
      if (prev[index] === hasError) return prev;
      const next = [...prev];
      next[index] = hasError;
      return next;
    });
  };

  const INSURER_FORMAT_REGEX = /^\d+-\d+$/;

  const handleFormSubmit = handleSubmit(
    async (data: InsurerAttentionUpdateFormData) => {
      const trimmed = insurerDisplay.trim();
      if (!trimmed || !INSURER_FORMAT_REGEX.test(trimmed)) {
        setInsurerError('保険者番号の入力が不正です。');
        toastError('エラーが発生しました。');
        return;
      }

      let resolvedInsurerId = insurerId;

      if (!resolvedInsurerId) {
        const [insurerNumber, subNumber] = trimmed.split('-');
        try {
          const res = await getInsurerList(siteType, {
            search_insurer_number: insurerNumber,
            per_page: '100',
          });
          const match = res.data.data.find(
            ins => ins.insurer_number === insurerNumber && String(ins.sub_number) === subNumber
          );
          if (!match) {
            setInsurerError('保険者番号の入力が不正です。');
            toastError('エラーが発生しました。');
            return;
          }
          resolvedInsurerId = match.id;
          resolveInsurer(match.id, match.insurer_type?.name ?? '', match.insurer_name ?? '');
        } catch {
          setInsurerError('保険者番号の入力が不正です。');
          toastError('エラーが発生しました。');
          return;
        }
      }

      setInsurerError(null);
      if (isUploading) {
        toastError('ファイルをアップロード中です。');
        return;
      }
      if (hasUploadValidationError) {
        toastError('エラーが発生しました。');
        return;
      }

      const formData: AdminInsurerAttentionUpdateForm = {
        insurer_id: resolvedInsurerId,
        prefecture_id: Number(data.prefecture_id),
        publish_date: data.publish_date,
        remarks: data.remarks,
      };

      for (let i = 0; i < ATTACHMENT_COUNT; i++) {
        const n = i + 1;
        if (newRefs[i]) {
          Object.assign(formData, { [`attachment${n}`]: newRefs[i] });
        } else if (deleted[i]) {
          Object.assign(formData, { [`delete_attachment${n}`]: true });
        }
      }

      onSubmit(formData);
    },
    () => {
      const trimmed = insurerDisplay.trim();
      if (!trimmed || !INSURER_FORMAT_REGEX.test(trimmed) || !insurerId) {
        setInsurerError('保険者番号の入力が不正です。');
      }
      toastError('エラーが発生しました。');
    }
  );

  return (
    <>
      <AppCard title={`県別注意事項編集:${record.id}`}>
        <form
          id="insurer-attention-edit-form"
          onSubmit={handleFormSubmit}
          className="space-y-5 w-full"
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <AppAdminFormLabel label="保険者番号" required />
              <div className="flex gap-2 items-center">
                <AppTextField
                  value={insurerDisplay}
                  onChange={e => {
                    setInsurerDisplay(e.target.value);
                    if (insurerError) setInsurerError(null);
                  }}
                  status={insurerError ? 'error' : undefined}
                  className="flex-1"
                />
                <AppButton type="primary" loading={isOpeningModal} onClick={handleOpenInsurerModal}>
                  補助入力
                </AppButton>
              </div>
              {insurerError && <p className="text-red-500 text-small mt-1">{insurerError}</p>}
            </div>

            <div className="flex-1">
              <AppAdminFormLabel label="保険種別" />
              <AppTextField value={insurerTypeName} disabled className="w-full" />
            </div>

            <div className="flex-1">
              <AppAdminFormLabel label="保険者名称" />
              <AppTextField value={insurerName} disabled className="w-full" />
            </div>
          </div>

          <div>
            <AppAdminFormLabel label="都道府県" required />
            <Controller
              name="prefecture_id"
              control={control}
              render={({ field }) => (
                <AppSelect
                  className="w-full"
                  options={prefectureOptions}
                  value={field.value ? Number(field.value) : undefined}
                  onChange={val => {
                    field.onChange(val ? String(val) : '');
                    clearErrors('prefecture_id');
                  }}
                  status={errors.prefecture_id ? 'error' : undefined}
                  placeholder=""
                />
              )}
            />
            {errors.prefecture_id && (
              <p className="text-red-500 text-small mt-1">{errors.prefecture_id.message}</p>
            )}
          </div>

          <div>
            <AppAdminFormLabel label="掲載日" required />
            <Controller
              name="publish_date"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  value={field.value || null}
                  onChange={value => {
                    field.onChange(dateValueToIso(value) ?? '');
                    clearErrors('publish_date');
                  }}
                  error={!!errors.publish_date}
                  placeholder=""
                  className="w-full"
                />
              )}
            />
            {errors.publish_date && (
              <p className="text-red-500 text-small mt-1">{errors.publish_date.message}</p>
            )}
          </div>

          <div>
            <AppAdminFormLabel label="注意事項" required />
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <AppTextField
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.target.value);
                    clearErrors('remarks');
                  }}
                  status={errors.remarks ? 'error' : undefined}
                  className="w-full"
                />
              )}
            />
            {errors.remarks && (
              <p className="text-red-500 text-small mt-1">{errors.remarks.message}</p>
            )}
          </div>

          <div>
            <AppAdminFormLabel label="資料" />
            <div className="space-y-3">
              {Array.from({ length: ATTACHMENT_COUNT }, (_, i) => {
                const showExisting = !!currentNames[i] && !deleted[i] && !newRefs[i];
                if (showExisting) {
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm truncate">{currentNames[i]}</span>
                      <CloseOutlined
                        className="text-red-500 hover:text-red-700 cursor-pointer transition-colors text-xs"
                        onClick={() => handleAttachmentDelete(i)}
                      />
                    </div>
                  );
                }
                return (
                  <PresignedUploadField
                    key={i}
                    context="insurer_attention_attachment"
                    value={newRefs[i] ?? null}
                    onChange={val => handleSlotChange(i, val)}
                    onUploadingChange={flag => handleSlotUploading(i, flag)}
                    onValidationErrorChange={hasError => handleSlotValidationError(i, hasError)}
                    className="border border-gray-300 rounded px-2 py-2"
                  />
                );
              })}
            </div>
          </div>
        </form>
      </AppCard>

      <div className="flex justify-end gap-3 mt-4">
        <AppButton danger onClick={onDelete}>
          削除
        </AppButton>

        <AppButton
          type="primary"
          onClick={onReflect}
          style={
            {
              backgroundColor: colors.secondary,
              ['--action-bg-hover' as string]: colors.secondaryHover,
            } as React.CSSProperties
          }
          className="hover:!bg-[var(--action-bg-hover)]"
        >
          データ反映
        </AppButton>
        <AppButton
          onClick={onBack}
        >
          前画面に戻る
        </AppButton>
        <AppButton
          type="primary"
          htmlType="submit"
          form="insurer-attention-edit-form"
          loading={isPending}
          disabled={isUploading}
        >
          <span className="px-6">更新</span>
        </AppButton>
      </div>

      <AppModal
        title="保険者検索"
        isOpen={isInsurerModalOpen}
        onClose={() => setIsInsurerModalOpen(false)}
        maxWidth="max-w-4xl"
        footer={
          <AppButton type="primary" onClick={() => setIsInsurerModalOpen(false)}>
            閉じる
          </AppButton>
        }
      >
        <InsuranceCompanySearch
          onSelect={handleInsurerSelect}
          initialSearch={insurerDisplay.trim()}
        />
      </AppModal>
    </>
  );
};

export default AdminInsurerAttentionEditForm;
